import React, { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button, { IconButtonProps } from '@ui/Button';
import Icon from '@ui/Icon';
import { useCanReadCustomWorkspace } from 'abilities/workspaces';
import LoadingIcon from '../../../LoadingIndicators/CircularLoader';
import { usePromptsContext } from '../../../../context/prompts/PromptsContext';
import useClipCopy from '../../../../context/clips/useClipCopy';
import { Project } from '../../../../../server/graphql/__generated.types';

export interface CopyClipsButtonProps
  extends Omit<IconButtonProps, 'aria-label' | 'children'> {
  project: Project;
  clipIds: Array<string>;
  className?: string;
  hideLabel?: boolean;
}

const useStyles = makeStyles()(theme => ({
  button: {
    padding: theme.spacing(0, 0, 0, 0.25),
  },
}));

/**
 * This component allows the user to copy a subset of audio files in a project into another project.
 */
const CopyClipsButton = (props: CopyClipsButtonProps) => {
  const { clipIds, className, project, disabled, hideLabel, ...rest } = props;
  const { classes, cx } = useStyles();
  const [, promptsDispatch] = usePromptsContext();
  const [copying, setCopying] = React.useState(false);
  const hasWorkspaces = useCanReadCustomWorkspace();

  const onCopyStart = useCallback(() => {
    setCopying(true);
  }, []);

  const onCopyEnd = useCallback(() => {
    setCopying(false);
  }, []);

  const [submitFn] = useClipCopy(clipIds, onCopyStart, onCopyEnd);

  const dispatchCopyClipsModal = useCallback(() => {
    promptsDispatch({
      type: 'OpenCopyClipsToProjectPrompt',
      payload: {
        clipIds,
        project,
        submitFunction: submitFn,
        hasWorkspaces,
      },
    });
  }, [promptsDispatch, clipIds, project, submitFn, hasWorkspaces]);

  const copyButtonLabel = hideLabel ? undefined : 'Copy';

  return (
    <Button
      {...rest}
      variant="icon"
      title="Copy Clips"
      aria-label="copy"
      label={copyButtonLabel}
      disabled={copying || disabled}
      onClick={dispatchCopyClipsModal}
      className={cx(classes.button, className)}
    >
      {copying ? <LoadingIcon /> : <Icon type="copy" />}
    </Button>
  );
};

export default CopyClipsButton;
